import React, { useState, useLayoutEffect, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AnnotationModal, { Annotation } from './components/AnnotationModal';
import ConfirmModal from './components/ConfirmModal';
import PlanSelector from './components/PlanSelector';
import hombreImg from './images/hombre.png';
import sentadoImg from './images/sentado.png';

const WEEK_DAYS = ['LUNES','MARTES','MIÉRCOLES','JUEVES','VIERNES','SÁBADO','DOMINGO'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Estilos CSS para el ajuste automático del texto
const monthSelectorStyles = `
  .month-selector-text {
    font-size: clamp(0.75rem, 2.5vw, 1.125rem);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
    min-width: 120px;
  }
`;

const annotationsData: Annotation[] = [
    {
        day: 20,
        month: 5, // Junio (0-indexed)
        year: 2025,
        title: "Promociones de verano",
        description: "¡Sumérgete en la frescura de la primavera con nuestras ofertas especiales! Cada plato es una obra maestra que combina sabores vibrantes y creatividad culinaria.",
        images: [sentadoImg, hombreImg]
    },
    {
        day: 14,
        month: 5, // Junio
        year: 2025,
        title: "Post: \"Recetas saludables para el almuerzo...\"",
        description: "Una colección de recetas fáciles y deliciosas para un almuerzo lleno de energía.",
        images: [hombreImg]
    }
];

interface Post {
  id: number;
  postDate: string;
  title: string;
  description: string;
  images: string[];
  userId: number;
  createdAt: string;
  updatedAt: string;
}

function getCalendarDays(year: number, month: number) {
  // month: 0-indexed (0=enero)
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  // JS: 0=domingo, 1=lunes...
  let startDay = firstDayOfMonth.getDay();
  if (startDay === 0) startDay = 7; // Para que lunes sea 1, domingo 7

  // Días del mes anterior a mostrar
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();
  const prevDays = [];
  for (let i = startDay - 1; i > 0; i--) {
    prevDays.push({
      date: new Date(prevYear, prevMonth, daysInPrevMonth - i + 1),
      currentMonth: false
    });
  }

  // Días del mes actual
  const thisDays = [];
  for (let i = 1; i <= daysInMonth; i++) {
    thisDays.push({
      date: new Date(year, month, i),
      currentMonth: true
    });
  }

  // Días del mes siguiente a mostrar
  const days = [...prevDays, ...thisDays];
  const nextDays = [];
  let i = 1;
  // Completar hasta el siguiente múltiplo de 7 (puede ser 35 o 42)
  while ((days.length + nextDays.length) % 7 !== 0 || (days.length + nextDays.length) < 35) {
    nextDays.push({
      date: new Date(year, month + 1, i),
      currentMonth: false
    });
    i++;
    // Si ya hay 35 días y el total es múltiplo de 7, paramos (para meses de 5 semanas)
    if ((days.length + nextDays.length) >= 35 && (days.length + nextDays.length) % 7 === 0) break;
  }

  return [...days, ...nextDays];
}

// Función para obtener el lunes de la semana de una fecha
function getMonday(date: Date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - ((day + 6) % 7); // Lunes=1, Domingo=0
  d.setDate(diff);
  d.setHours(0,0,0,0);
  return d;
}

// Función para obtener el primer día del mes
function getFirstDayOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1, 0, 0, 0, 0);
}

// Función para obtener el último día del mes
function getLastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

// Modal de límite de posts
const LimitModal = ({ onClose, nextAvailableDate }: { onClose: () => void, nextAvailableDate: string }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
    <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center relative transform transition-all duration-300 scale-100">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-[#44aaf6] text-2xl font-bold focus:outline-none transition-colors duration-200"
        aria-label="Cerrar"
      >
        ×
      </button>
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
        <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold mb-3 text-gray-800">Límite mensual alcanzado</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Has alcanzado el límite de publicaciones para tu mensualidad.<br />
        La siguiente comienza a partir del día que indica la 
        <span className="inline-flex items-center font-bold text-[#44aaf6] ml-1">
          marca azul
        </span>.
      </p>
      <button
        onClick={onClose}
        className="w-full px-6 py-3 rounded-2xl bg-[#44aaf6] text-white font-semibold text-lg hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
      >
        Entendido
      </button>
    </div>
  </div>
);

const CalendarScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Estado para mes y año, inicializados desde location.state si existen o fecha actual
  const currentDate = new Date();
  const initialYear = location.state?.year ?? currentDate.getFullYear();
  const initialMonth = location.state?.month ?? currentDate.getMonth();
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth); // 0-indexed
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]); // Posts reales
  const [isPostsLoading, setIsPostsLoading] = useState(false); // Estado para controlar carga de posts
  const [scheduledDates, setScheduledDates] = useState<{currentMonthDates: string[], nextMonthDates: string[]} | null>(null); // Fechas automáticas
  const days = getCalendarDays(year, month);
  const monthTextRef = useRef<HTMLSpanElement>(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState<Post | null>(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [adminViewUser, setAdminViewUser] = useState<{id: number, email: string, role?: string, plan?: string, createdAt?: string} | null>(null);
  const [currentUser, setCurrentUser] = useState<{id: number, email: string, role: string, plan?: string, createdAt?: string} | null>(null);
  const [expandedPostId, setExpandedPostId] = useState<number | null>(null);
  const [highlightedPostId, setHighlightedPostId] = useState<number | null>(null);
  const postRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastObservedId = useRef<number | null>(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [nextAvailableDate, setNextAvailableDate] = useState<string>('');
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [limitError, setLimitError] = useState<string>('');
  const [planChangeKey, setPlanChangeKey] = useState(0); // Para forzar re-render cuando cambie el plan
  
  // Estado para la pantalla de carga
  const [isLoadingDay, setIsLoadingDay] = useState(false);
  
  // Estado para el modal de confirmación de borrado
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [postToDelete, setPostToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Estado para el rango de meses cargados (cache)
  const [loadedRange, setLoadedRange] = useState<{startYear: number, startMonth: number, endYear: number, endMonth: number} | null>(null);

  // 1. Añadir estado para el popup de advertencia
  const [showDateWarning, setShowDateWarning] = useState(false);
  // 1. Añadir estado para el popup de advertencia de antelación
  const [showAdvanceWarning, setShowAdvanceWarning] = useState(false);
  // Estado de carga del usuario objetivo (admin)
  const [isUserLoading, setIsUserLoading] = useState(!!location.state?.adminViewUserId);
  // Estado para animación de fade del overlay
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(isUserLoading);
  const [fadeOutLoading, setFadeOutLoading] = useState(false);
  // Estado para controlar cuándo está listo para hacer fetch de posts
  const [isReadyToFetch, setIsReadyToFetch] = useState(false);

  // Obtener información del usuario actual
  useEffect(() => {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem('user') || 'null');
    } catch {}
    if (user) {
      setCurrentUser(user);
    }
  }, []);

  // Activar carga de posts si viene de ContentCreationScreen
  useEffect(() => {
    if (location.state?.fromContentCreation) {
      setIsPostsLoading(true);
    }
  }, [location.state?.fromContentCreation]);

  // Comprobar si es admin y si debe redirigir
  useEffect(() => {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem('user') || 'null');
    } catch {}
    if (user && user.role === 'ADMIN') {
      // Si no viene de selección de usuario, redirigir a /admin-users
      if (!location.state?.adminViewUserId) {
        navigate('/admin-users');
      } else {
        setIsReadOnly(false); // El admin puede crear posts
        const adminUser = { id: location.state.adminViewUserId, email: location.state.adminViewUserEmail, plan: location.state.adminViewUserPlan };
        setAdminViewUser(adminUser);
        // Si no hay que cargar más datos, desactivar loading
        if (!location.state.adminViewUserId) setIsUserLoading(false);
      }
    }
  }, [location, navigate]);

  // Si hay adminViewUserId, obtener info completa del usuario seleccionado
  useEffect(() => {
    if (location.state?.adminViewUserId) {
      setIsUserLoading(true); // Activar loading al iniciar fetch
      const fetchUser = async () => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${location.state.adminViewUserId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAdminViewUser(data);
        } else {
          setAdminViewUser({
            id: location.state.adminViewUserId,
            email: location.state.adminViewUserEmail,
            plan: location.state.adminViewUserPlan
          });
        }
        setIsUserLoading(false); // Desactivar loading al terminar
      };
      fetchUser();
    } else {
      setIsUserLoading(false); // Si no hay adminViewUserId, no hay loading
    }
  }, [location.state]);

  // Controlar cuándo está listo para hacer fetch de posts
  useEffect(() => {
    if (location.state?.adminViewUserId) {
      // Solo marcar como ready cuando se complete la carga del usuario Y la búsqueda de posts
      if (!isUserLoading && adminViewUser && !isPostsLoading) {
        setIsReadyToFetch(true);
      }
    } else {
      // Si no es admin view, marcar como ready inmediatamente
      setIsReadyToFetch(true);
    }
  }, [location.state, isUserLoading, adminViewUser, isPostsLoading]);

  // Resetear el cache cuando cambie el usuario
  useEffect(() => {
    setLoadedRange(null);
  }, [adminViewUser?.id]);

  // Manejar fade-out del overlay de carga
  useEffect(() => {
    if (isUserLoading) {
      setShowLoadingOverlay(true);
      setFadeOutLoading(false);
    } else if (showLoadingOverlay) {
      setFadeOutLoading(true);
      const timeout = setTimeout(() => {
        setShowLoadingOverlay(false);
        setFadeOutLoading(false);
      }, 350); // Duración del fade
      return () => clearTimeout(timeout);
    }
  }, [isUserLoading]);

  // Reemplaza getRange por lo siguiente:
  const getExtendedRange = () => {
    // Mes anterior 2
    let startMonth = month - 2;
    let startYear = year;
    if (startMonth < 0) {
      startYear -= 1;
      startMonth += 12;
    }
    const start = new Date(startYear, startMonth, 1, 0, 0, 0, 0);

    // Mes posterior 2
    let endMonth = month + 2;
    let endYear = year;
    if (endMonth > 11) {
      endYear += 1;
      endMonth -= 12;
    }
    // Último día del mes posterior 2
    const end = new Date(endYear, endMonth + 1, 0, 23, 59, 59, 999);

    return {
      start: start.toISOString(),
      end: end.toISOString(),
      startYear,
      startMonth,
      endYear,
      endMonth
    };
  };

  // Función para verificar si el mes actual está en el rango cargado
  const isMonthInLoadedRange = (currentYear: number, currentMonth: number): boolean => {
    if (!loadedRange) return false;
    
    const currentDate = new Date(currentYear, currentMonth, 1);
    const rangeStart = new Date(loadedRange.startYear, loadedRange.startMonth, 1);
    const rangeEnd = new Date(loadedRange.endYear, loadedRange.endMonth, 1);
    
    return currentDate >= rangeStart && currentDate <= rangeEnd;
  };

  // Función para verificar si una fecha es una fecha automática
  const isScheduledDate = (date: Date): boolean => {
    if (!scheduledDates) return false;
    if (!Array.isArray(scheduledDates.currentMonthDates) || !Array.isArray(scheduledDates.nextMonthDates)) return false;
    if (scheduledDates.currentMonthDates.length === 0 && scheduledDates.nextMonthDates.length === 0) return false;
    
    // Normalizar la fecha del calendario a zona horaria de España y restar un día
    const dateInSpain = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
    dateInSpain.setDate(dateInSpain.getDate() +1);
    const dateString = dateInSpain.toISOString().split('T')[0]; // YYYY-MM-DD
    
    // Verificar en fechas del mes actual
    const currentMonthMatch = scheduledDates.currentMonthDates.some(scheduledDate => {
      // Normalizar la fecha programada a zona horaria de España
      const scheduledDateInSpain = new Date(new Date(scheduledDate).toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
      const scheduledDateString = scheduledDateInSpain.toISOString().split('T')[0];
      return scheduledDateString === dateString;
    });
    
    // Verificar en fechas del próximo mes
    const nextMonthMatch = scheduledDates.nextMonthDates.some(scheduledDate => {
      // Normalizar la fecha programada a zona horaria de España
      const scheduledDateInSpain = new Date(new Date(scheduledDate).toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
      const scheduledDateString = scheduledDateInSpain.toISOString().split('T')[0];
      return scheduledDateString === dateString;
    });
    
    return currentMonthMatch || nextMonthMatch;
  };

  // En el useEffect de carga de posts:
  useEffect(() => {
    // Solo hacer fetch si tenemos el usuario admin o si no es admin view
    if (location.state?.adminViewUserId && !adminViewUser) return;
    
    // OPTIMIZACIÓN: Solo hacer fetch si el mes NO está en el rango cargado
    if (isMonthInLoadedRange(year, month)) {
      console.log('✨ Mes ya cargado en cache, no hacer fetch:', { year, month, loadedRange });
      return;
    }
    
    console.log('🔄 Cargando nuevo rango de meses:', { year, month });
    
    const fetchPosts = async () => {
      setIsPostsLoading(true); // Activar loading al iniciar fetch
      const token = localStorage.getItem('token');
      const { start, end, startYear, startMonth, endYear, endMonth } = getExtendedRange();
      let url = `${process.env.REACT_APP_API_URL}/posts?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
      if (adminViewUser) {
        url += `&userId=${adminViewUser.id}`;
      }
      try {
        const res = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setPosts(data);
          // Actualizar el rango cargado
          setLoadedRange({ startYear, startMonth, endYear, endMonth });
          console.log('✅ Posts cargados, nuevo rango:', { startYear, startMonth, endYear, endMonth });
        } else {
          setPosts([]);
        }
      } catch (err) {
        setPosts([]);
      } finally {
        setIsPostsLoading(false); // Desactivar loading al terminar
      }
    };
    
    const fetchScheduledDates = async () => {
      try {
        const token = localStorage.getItem('token');
        let url = `${process.env.REACT_APP_API_URL}/scheduled-posts/my-dates`;
        
        // Si es admin view, agregar el userId del usuario que está viendo
        if (adminViewUser) {
          url += `?userId=${adminViewUser.id}`;
        }
        
        const res = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setScheduledDates(data);
        } else {
          setScheduledDates(null);
        }
      } catch (err) {
        setScheduledDates(null);
      }
    };
    
    fetchPosts();
    fetchScheduledDates();
  }, [year, month, adminViewUser, isReadyToFetch, loadedRange]);

  useEffect(() => {
    // Esperar a que se complete la carga del usuario (si es admin) y de los posts
    const checkLoadingComplete = () => {
      const userLoadingComplete = !isUserLoading;
      const postsLoadingComplete = !isPostsLoading;
      
      if (userLoadingComplete && postsLoadingComplete) {
        const timer = setTimeout(() => {
          setIsLoaded(true);
        }, 100);
        return () => clearTimeout(timer);
      }
    };
    
    checkLoadingComplete();
  }, [isUserLoading, isPostsLoading]);

  useLayoutEffect(() => {
    const el = monthTextRef.current;
    if (el) {
        const containerWidth = 120;
        
        let fontSize = 18; // Max font size (1.125rem)
        el.style.fontSize = `${fontSize}px`;
        
        // Decrease font size until it fits
        while (el.scrollWidth > containerWidth && fontSize > 12) {
            fontSize -= 0.5;
            el.style.fontSize = `${fontSize}px`;
        }
    }
  }, [month, year]);

  // Calcular el mes mínimo permitido (mes anterior al actual)
  const todayDate = new Date();
  const minMonth = todayDate.getMonth() - 1 < 0 ? 11 : todayDate.getMonth() - 1;
  const minYear = todayDate.getMonth() - 1 < 0 ? todayDate.getFullYear() - 1 : todayDate.getFullYear();

  // Función para comprobar si un mes/año es anterior al mínimo permitido
  function isBeforeMinMonth(yearToCheck: number, monthToCheck: number) {
    return yearToCheck < minYear || (yearToCheck === minYear && monthToCheck < minMonth);
  }

  // Modificar handlePrevMonth para evitar retroceder más allá del mes mínimo
  const handlePrevMonth = () => {
    let prevMonth = month === 0 ? 11 : month - 1;
    let prevYear = month === 0 ? year - 1 : year;
    if (isBeforeMinMonth(prevYear, prevMonth)) {
      return; // No permitir retroceder más allá del mes mínimo
    }
    if (month === 0) {
      setMonth(11);
      setYear((y: number) => y - 1);
    } else {
      setMonth((m: number) => m - 1);
    }
  };
  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y: number) => y + 1);
    } else {
      setMonth((m: number) => m + 1);
    }
  };

  const handleLogout = () => {
    setIsExiting(true);
    setTimeout(() => {
      navigate('/thank-you');
    }, 350);
  };

  const handleCloseModal = () => {
    setSelectedAnnotation(null);
  };

  const handleDeletePost = async (postId: number) => {
    // Mostrar modal de confirmación
    setPostToDelete(postId);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePost = async () => {
    if (!postToDelete) return;

    setIsDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${postToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Cerrar el modal de preview
        setSelectedAnnotation(null);
        
        // Actualizar la lista de posts eliminando el post borrado
        setPosts(prevPosts => prevPosts.filter(post => post.id !== postToDelete));
        
        // Recargar las fechas programadas automáticas (AUTO)
        const fetchScheduledDates = async () => {
          try {
            const token = localStorage.getItem('token');
            let url = `${process.env.REACT_APP_API_URL}/scheduled-posts/my-dates`;
            
            // Si es admin view, agregar el userId del usuario que está viendo
            if (adminViewUser) {
              url += `?userId=${adminViewUser.id}`;
            }
            
            const res = await fetch(url, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            if (res.ok) {
              const data = await res.json();
              setScheduledDates(data);
            } else {
              setScheduledDates(null);
            }
          } catch (err) {
            setScheduledDates(null);
          }
        };
        
        // Ejecutar la recarga de fechas programadas
        fetchScheduledDates();
      } else {
        const errorData = await response.json();
        console.error('Error al borrar el post:', errorData);
        // Mostrar error específico en modal
        let errorMessage = 'Error al borrar la publicación';
        if (errorData.error) {
          errorMessage = errorData.error;
        }
        setDeleteError(errorMessage);
      }
    } catch (error) {
      console.error('Error de red al borrar el post:', error);
      setDeleteError('Error de conexión al borrar la publicación');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setPostToDelete(null);
    }
  };

  const cancelDeletePost = () => {
    setShowDeleteConfirm(false);
    setPostToDelete(null);
    setIsDeleting(false);
  };

  const handlePlanChange = (accountId: number) => {
    // Resetear cache antes de recargar
    setLoadedRange(null);
    // Forzar re-render del componente
    setPlanChangeKey(prev => prev + 1);
    // Recargar la información del usuario y posts
    window.location.reload();
  };

  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  // Modificar monthsToRender para no mostrar meses anteriores al mínimo permitido
  const monthsToRender = [-2, -1, 0, 1, 2].map(offset => {
    let m = month + offset;
    let y = year;
    if (m < 0) {
      m += 12;
      y -= 1;
    } else if (m > 11) {
      m -= 12;
      y += 1;
    }
    return { month: m, year: y };
  }).filter(({ month: m, year: y }) => !isBeforeMinMonth(y, m));

  // Observar la visibilidad de la tarjeta expandida
  useEffect(() => {
    if (expandedPostId == null) return;
    const el = postRefs.current[expandedPostId];
    if (!el) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new window.IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && expandedPostId != null) {
            setHighlightedPostId(expandedPostId);
            setTimeout(() => {
              setHighlightedPostId(null);
            }, 700);
            if (observerRef.current) observerRef.current.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    observerRef.current.observe(el);
    lastObservedId.current = expandedPostId;
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [expandedPostId]);

  // Al hacer clic en día o lista de posts, solo expandir y hacer scroll, el fade lo dispara el observer
  const handleExpandAndScroll = useCallback((postId: number) => {
    setExpandedPostId(postId);
    setTimeout(() => {
      const ref = postRefs.current[postId];
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  // Manejar clic en día del calendario (solo móvil)
  const handleDayClick = async (postId: number | null, date: Date, isCurrent: boolean) => {
    if (postId) {
      handleExpandAndScroll(postId);
    } else if (isCurrent && !checkingLimit) {
      // Mostrar pantalla de carga inmediatamente
      setIsLoadingDay(true);
      setCheckingLimit(true);
      setLimitError('');
      // Obtener el usuario activo (admin view o usuario actual)
      const userId = adminViewUser?.id || currentUser?.id;
      if (!userId) {
        setCheckingLimit(false);
        setIsLoadingDay(false);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/limit-status?userId=${userId}&date=${encodeURIComponent(date.toISOString())}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (!data.canCreate) {
            // Calcular la fecha siguiente disponible (period.end + 1 día)
            const end = new Date(data.period.end);
            end.setDate(end.getDate() + 1);
            const nextDate = end.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
            setNextAvailableDate(nextDate);
            setShowLimitModal(true);
            setCheckingLimit(false);
            setIsLoadingDay(false);
            return;
          }
        } else {
          setLimitError('No se pudo comprobar el límite. Inténtalo de nuevo.');
          setCheckingLimit(false);
          setIsLoadingDay(false);
          return;
        }
      } catch (err) {
        setLimitError('Error de red al comprobar el límite.');
        setCheckingLimit(false);
        setIsLoadingDay(false);
        return;
      }
      setIsExiting(true);
      setTimeout(() => {
        setIsLoadingDay(false); // Ocultar pantalla de carga antes de navegar
        navigate('/create-content', { 
          state: { 
            postDate: date.toISOString(), 
            originYear: year, 
            originMonth: month,
            adminViewUserId: adminViewUser?.id,
            adminViewUserEmail: adminViewUser?.email,
            adminViewUserPlan: adminViewUser?.plan
          } 
        });
        setCheckingLimit(false);
      }, 350);
    }
  };

  // 2. Calcular días de inicio de mensualidad
  function getStartOfBillingPeriods(createdAt: string, monthsToRender: {month: number, year: number}[]) {
    if (!createdAt) return [];
    const starts: string[] = [];
    const created = new Date(createdAt);
    // Calcular el rango de meses a mostrar
    monthsToRender.forEach(({ month, year }) => {
      // Para cada mes, calcular el día de inicio de mensualidad
      let start = new Date(year, month, created.getDate());
      // Si el mes no tiene ese día (ej: 31 de febrero), usar el último día del mes
      if (start.getMonth() !== month) {
        start = new Date(year, month + 1, 0);
      }
      starts.push(start.toISOString().slice(0, 10));
    });
    return starts;
  }

  // 3. Obtener los días de inicio de mensualidad a marcar
  const userCreatedAt = adminViewUser?.createdAt || currentUser?.createdAt;
  const billingStarts = userCreatedAt ? getStartOfBillingPeriods(userCreatedAt, monthsToRender) : [];

  // Determinar si el botón de mes anterior debe estar deshabilitado
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const isPrevMonthDisabled = isBeforeMinMonth(prevYear, prevMonth);

  // Función para obtener la fecha de hoy en España (solo fecha, sin hora)
  function getTodayInSpain() {
    const now = new Date();
    // Convertir a zona horaria de España (Europe/Madrid)
    const spainDate = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
    return spainDate;
  }

  // Función para verificar si una fecha es hoy en España (solo fecha, sin hora)
  function isTodayInSpain(date: string) {
    const today = getTodayInSpain();
    const postDate = new Date(date);
    
    // Convertir postDate a hora de España y extraer solo la fecha (sin hora)
    const postDateInSpain = new Date(postDate.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
    const todayInSpain = new Date(today.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
    
    // Comparar solo año, mes y día (ignorar horas)
    return postDateInSpain.getFullYear() === todayInSpain.getFullYear() &&
           postDateInSpain.getMonth() === todayInSpain.getMonth() &&
           postDateInSpain.getDate() === todayInSpain.getDate();
  }

  // Función para saber si un post se puede borrar (fecha > hoy, solo fecha española)
  function canDeletePost(postDateStr: string) {
    // Los usuarios ADMIN pueden borrar posts sin restricción de fecha
    if (currentUser?.role === 'ADMIN' || adminViewUser) {
      return true;
    }
    
    // Para usuarios normales, solo permitir borrar posts de fechas futuras (no hoy ni pasadas)
    // Usar la misma lógica que los webhooks: solo comparar fecha, no hora
    const today = getTodayInSpain();
    const postDate = new Date(postDateStr);
    
    // Convertir postDate a hora de España y extraer solo la fecha (sin hora)
    const postDateInSpain = new Date(postDate.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
    const todayInSpain = new Date(today.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
    
    // Comparar solo año, mes y día (ignorar horas)
    const postYear = postDateInSpain.getFullYear();
    const postMonth = postDateInSpain.getMonth();
    const postDay = postDateInSpain.getDate();
    const todayYear = todayInSpain.getFullYear();
    const todayMonth = todayInSpain.getMonth();
    const todayDay = todayInSpain.getDate();
    
    // Permitir borrar solo si la fecha del post es posterior a hoy
    if (postYear > todayYear) return true;
    if (postYear < todayYear) return false;
    if (postMonth > todayMonth) return true;
    if (postMonth < todayMonth) return false;
    return postDay > todayDay;
  }

  return (
    <div className="relative min-h-screen bg-[#F8F9FA]">
      {/* Overlay de carga/deshabilitado para adminViewUser con fade */}
      {showLoadingOverlay && (
        <div
          className={`fixed inset-0 z-[9999] flex items-center justify-center bg-gray-200 bg-opacity-80 transition-all duration-300 ${fadeOutLoading ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'}`}
          style={{ pointerEvents: 'all' }}
        >
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-12 w-12 text-[#44aaf6] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-lg text-[#44aaf6] font-semibold">Cargando calendario...</span>
          </div>
        </div>
      )}
      
      {/* Pantalla de carga para días del calendario */}
      {isLoadingDay && (
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center bg-gray-200 bg-opacity-80 transition-all duration-500 ease-out"
          style={{ pointerEvents: 'all' }}
        >
          <div className="flex flex-col items-center animate-fadeIn">
            <svg className="animate-spin h-12 w-12 text-[#44aaf6] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-lg text-[#44aaf6] font-semibold">Cargando datos...</span>
          </div>
        </div>
      )}
      {/* VERSIÓN MÓVIL */}
      {isReadyToFetch && (
        <div
          className={`block sm:hidden bg-[#F8F9FA] min-h-screen w-full font-futura px-0 pb-8 relative transition-all duration-700 ease-in-out ${isExiting ? 'opacity-0' : isLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ pointerEvents: isLoaded && !isExiting ? 'auto' : 'none' }}
        >
        {/* Spinner de carga */}
        {!isLoaded && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#F8F9FA] bg-opacity-80">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full border-4 border-blue-200 border-t-[#44aaf6] w-16 h-16 mb-4" style={{ borderTopColor: '#44aaf6', borderRightColor: '#e0e7ef', borderBottomColor: '#e0e7ef', borderLeftColor: '#e0e7ef' }}></div>
              <span className="text-[#44aaf6] font-bold text-lg tracking-wide">Cargando calendario…</span>
            </div>
          </div>
        )}
        {/* Botón Cerrar sesión */}
        <div className="flex justify-between items-center px-4 pt-6 gap-2 block sm:hidden">
          <PlanSelector onPlanChange={handlePlanChange} />
          <button 
            onClick={handleLogout}
            className="px-4 py-1.5 rounded-full bg-white shadow text-[#44aaf6] font-bold text-sm border border-white hover:bg-blue-50 transition-all"
            style={{letterSpacing: '0.08em'}}>
            Cerrar sesión
          </button>
        </div>
        {/* Selector de mes */}
        <div className="flex items-center justify-center mt-4 mb-4">
          <button
            onClick={isPrevMonthDisabled ? undefined : handlePrevMonth}
            className={`w-9 h-9 flex items-center justify-center rounded-full shadow text-xl transition
              ${isPrevMonthDisabled ? 'bg-gray-200 text-gray-300 cursor-not-allowed opacity-60' : 'bg-white text-gray-400 hover:bg-blue-50'}`}
            disabled={isPrevMonthDisabled}
            aria-disabled={isPrevMonthDisabled}
          >&#60;</button>
          <span
            ref={monthTextRef}
            className="font-semibold text-center mx-4 text-lg"
            style={{ minWidth: '100px' }}
          >
            {MONTHS[month]} {year}
          </span>
          <button onClick={handleNextMonth} className="w-9 h-9 flex items-center justify-center rounded-full bg-white shadow text-xl text-gray-400 hover:bg-blue-50 transition">&#62;</button>
        </div>
        {/* Calendario */}
        <div className="px-7">
          <div className="grid grid-cols-7 gap-1">
            {["LU","MA","MI","JU","VI","SA","DO"].map((d, i) => (
              <div key={i} className="text-center text-base font-helvetica-italic font-semibold text-gray-500 pb-1">{d}</div>
            ))}
          </div>
          {/* Renderizar los 5 calendarios, solo uno visible */}
          {monthsToRender.map(({ month: calMonth, year: calYear }, idx) => {
            const visible = calMonth === month && calYear === year;
            const days = getCalendarDays(calYear, calMonth);
            return (
              <div
                key={`${calYear}-${calMonth}`}
                className={`grid grid-cols-7 gap-1 mt-1 transition-opacity duration-300 ${visible ? '' : 'hidden'}`}
              >
                {days.map((d, idx) => {
                  const dayNum = d.date.getDate();
                  const isCurrent = d.currentMonth;
                  const post = posts.find((p: any) => {
                    const postDate = new Date(p.postDate);
                    return postDate.getFullYear() === d.date.getFullYear() &&
                      postDate.getMonth() === d.date.getMonth() &&
                      postDate.getDate() === d.date.getDate();
                  });
                  const isAnnotation = !!post;
                  const isBillingStart = billingStarts.includes(d.date.toISOString().slice(0, 10));
                  const isAutoScheduled = isScheduledDate(d.date);
                  // Detectar si es hoy
                  const isToday = (() => {
                    const today = new Date();
                    today.setHours(0,0,0,0);
                    const dateToCheck = new Date(d.date);
                    dateToCheck.setHours(0,0,0,0);
                    return today.getTime() === dateToCheck.getTime();
                  })();
                  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
                    if (isAnnotation) {
                      // Scroll y expandir post en móvil
                      setExpandedPostId(post.id);
                      setTimeout(() => {
                        const el = postRefs.current[post.id];
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 100);
                    } else if (isPastOrToday) {
                      setShowDateWarning(true);
                    } else {
                      handleDayClick(null, d.date, isCurrent);
                    }
                  };
                  const isPastOrToday = d.date < new Date(new Date().setHours(0,0,0,0)) || d.date.toDateString() === new Date().toDateString();
                  return (
                    <div
                      key={idx}
                      className={`flex flex-col items-center justify-center aspect-[6/7] rounded-xl select-none transition-all
                        ${isCurrent
                          ? (isAnnotation
                              ? 'bg-[#f4faff] border-2 border-blue-200 text-blue-400'
                              : 'bg-white text-gray-800 border border-gray-200')
                          : 'bg-[#f7f7f7] text-[#d1d5db] border border-gray-200'}
                        ${isPastOrToday ? 'opacity-50 pointer-events-none bg-gray-100 text-gray-400' : ''}
                      `}
                      style={{ minHeight: 30, minWidth: 38, maxWidth: 44 }}
                      onClick={!isPastOrToday ? handleClick : undefined}
                    >
                      <span className={
                        'font-helvetica-italic font-normal text-lg ' +
                        (isAnnotation ? 'font-bold text-blue-400' : '')
                      }>
                        {dayNum}
                        {isBillingStart && (
                          <span className="ml-2 align-middle text-[#44aaf6] font-bold text-2xl relative group" style={{ cursor: 'pointer', display: 'inline-block' }}>
                            •
                            <span className="hidden sm:block absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200" style={{ minWidth: '120px' }}>
                              <span className="relative bg-black text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap text-center block">
                                Inicio de mensualidad (reseteo de posts)
                                <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-5 border-r-5 border-t-5 border-l-transparent border-r-transparent border-t-black"></span>
                              </span>
                            </span>
                          </span>
                        )}
                      </span>
                      {/* Texto AUTO para fechas programadas automáticamente */}
                      {isAutoScheduled && !isAnnotation && (
                        <div className="text-[10px] text-blue-500 font-medium mt-0.5 leading-none">
                          AUTO.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {/* Barra separadora */}
        <div className="flex justify-center items-center mt-4 mb-12">
          <div className="w-24 h-1 rounded-full bg-black opacity-80"></div>
        </div>
        {/* Lista de posts */}
        {(() => {
          const postsOfMonth = posts.filter(post => {
            const date = new Date(post.postDate);
            return date.getFullYear() === year && date.getMonth() === month;
          });
          return (
            <div className="px-6 flex flex-col gap-3">
              {postsOfMonth.length === 0 ? (
                <div className="text-center text-gray-400 text-base py-8">No hay publicaciones este mes</div>
              ) : postsOfMonth.map((post) => (
                <div
                  key={post.id}
                  className="rounded-2xl border border-[#44aaf6] bg-[#f4faff] px-4 py-3 flex items-center gap-3 shadow-sm cursor-pointer"
                  onClick={() => handleExpandAndScroll(post.id)}
                >
                  <span className="text-[#44aaf6] font-bold text-lg min-w-[32px] text-center flex items-center gap-1">
                    {new Date(post.postDate).getDate()}
                    {post.images && post.images.length > 0 && (
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block align-middle">
                      <rect x="1" y="2" width="14" height="12" rx="2" stroke="#44aaf6" strokeWidth="1.5"/>
                      <circle cx="5.5" cy="6.5" r="1.5" stroke="#44aaf6" strokeWidth="1.5"/>
                      <path d="M10.5 14L8 11L15 4" stroke="#44aaf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    )}
                  </span>
                  <span className="font-bold text-gray-700 text-base mr-1">Post:</span>
                  <span className="text-gray-700 text-base truncate flex-1">“{post.title.length > 30 ? post.title.slice(0, 27) + '…' : post.title}”</span>
                </div>
              ))}
            </div>
          );
        })()}
        {/* Tarjetas detalladas de los posts */}
        <div className="mt-8 px-3 flex flex-col gap-6">
          {posts.filter(post => {
            const date = new Date(post.postDate);
            return date.getFullYear() === year && date.getMonth() === month;
          }).map((post) => {
            const expanded = expandedPostId === post.id;
            const highlighted = highlightedPostId === post.id;
            return (
              <div key={post.id} className="relative">
                <div
                  ref={el => postRefs.current[post.id] = el}
                  className={`bg-white rounded-3xl shadow flex flex-col overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer ${expanded ? 'max-h-[900px]' : 'max-h-[180px]'} p-0`}
                  onClick={() => handleExpandAndScroll(post.id)}
                >
                  <div className={`w-full transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${expanded ? 'h-auto' : 'h-32'} overflow-hidden`} style={expanded ? {maxHeight: 400} : {minHeight: 90, maxHeight: 130}}>
                    {/* LAZY LOADING: Solo renderizar imagen cuando está expandido */}
                    {expanded && post.images && post.images.length > 0 ? (
                      (() => {
                        const imgSrc = post.images[0];
                        if (imgSrc.startsWith('data:')) {
                          // Imagen en base64
                          return (
                            <img
                              src={imgSrc}
                              alt="Imagen post"
                              className="w-full max-h-[400px] transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
                              style={{
                                width: '100%',
                                aspectRatio: '1/1',
                                height: 'auto',
                                objectFit: 'cover',
                                borderTopLeftRadius: '1.5rem',
                                borderTopRightRadius: '1.5rem',
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                                transition: 'border-radius 0.7s, max-height 0.7s, aspect-ratio 0.7s cubic-bezier(0.4,0,0.2,1)'
                              }}
                            />
                          );
                        } else if (imgSrc.startsWith('/uploads/post-images/')) {
                          // Imagen local del servidor - construir URL completa
                          const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:3001';
                          const fullUrl = `${baseUrl}${imgSrc}`;
                          console.log('🔍 CalendarScreen - Imagen local:', { imgSrc, fullUrl, baseUrl, originalApiUrl: process.env.REACT_APP_API_URL });
                          return (
                            <img
                              src={fullUrl}
                              alt="Imagen post"
                              className="w-full max-h-[400px] transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
                              onError={(e) => {
                                console.error('❌ Error cargando imagen local:', fullUrl, e);
                              }}
                              onLoad={() => {
                                console.log('✅ Imagen local cargada correctamente:', fullUrl);
                              }}
                              style={{
                                width: '100%',
                                aspectRatio: '1/1',
                                height: 'auto',
                                objectFit: 'cover',
                                borderTopLeftRadius: '1.5rem',
                                borderTopRightRadius: '1.5rem',
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                                transition: 'border-radius 0.7s, max-height 0.7s, aspect-ratio 0.7s cubic-bezier(0.4,0,0.2,1)'
                              }}
                            />
                          );
                        } else if (imgSrc.startsWith('http')) {
                          // Imagen de Drive o remota
                          return (
                            <img
                              src={imgSrc}
                              alt="Imagen post"
                              className="w-full max-h-[400px] transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
                              style={{
                                width: '100%',
                                aspectRatio: '1/1',
                                height: 'auto',
                                objectFit: 'cover',
                                borderTopLeftRadius: '1.5rem',
                                borderTopRightRadius: '1.5rem',
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                                transition: 'border-radius 0.7s, max-height 0.7s, aspect-ratio 0.7s cubic-bezier(0.4,0,0.2,1)'
                              }}
                            />
                          );
                        } else {
                          // No imagen válida
                          return (
                            <div className="w-full h-[110px] sm:h-[130px] flex items-center justify-center bg-[#f4faff]" style={{borderTopLeftRadius: '1.5rem', borderTopRightRadius: '1.5rem'}}>
                              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="6" y="10" width="36" height="28" rx="4" stroke="#44aaf6" strokeWidth="2.5" fill="#e6f2fd" />
                                <path d="M14 30L20 24L28 32L34 26L40 32" stroke="#44aaf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                <circle cx="16.5" cy="18.5" r="2.5" fill="#44aaf6" />
                              </svg>
                            </div>
                          );
                        }
                      })()
                    ) : (
                      // Placeholder cuando NO está expandido - solo icono sin cargar imagen
                      <div className="w-full h-[110px] sm:h-[130px] flex items-center justify-center bg-[#f4faff] transition-all duration-700" style={{borderTopLeftRadius: '1.5rem', borderTopRightRadius: '1.5rem'}}>
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="6" y="10" width="36" height="28" rx="4" stroke="#44aaf6" strokeWidth="2.5" fill="#e6f2fd" />
                          <path d="M14 30L20 24L28 32L34 26L40 32" stroke="#44aaf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="16.5" cy="18.5" r="2.5" fill="#44aaf6" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className={`bg-white px-5 ${expanded ? 'py-4' : 'py-2'} transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]`}
                    style={highlighted ? { transition: 'background 0.7s' } : {}}>
                    {/* Etiqueta de generado automáticamente encima del título */}
                    {post.images && post.images.length === 2 && post.images[1] === '' && (
                      <div className="font-futura text-xs font-semibold mb-1" style={{ color: '#44aaf6', letterSpacing: '0.04em' }}>
                        [ Generado automáticamente ]
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-lg text-gray-800 mb-1">{post.title}</div>
                      {/* Botón de eliminar (solo si está expandido y permitido) */}
                      {expanded && canDeletePost(post.postDate) && (
                        <button
                          onClick={e => { e.stopPropagation(); handleDeletePost(post.id); }}
                          className="ml-2 w-8 h-8 flex items-center justify-center bg-red-500/80 hover:bg-red-500 rounded-full shadow-md text-white transition-all duration-200 backdrop-blur-sm"
                          aria-label="Eliminar publicación"
                          title="Eliminar publicación"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="text-gray-400 text-sm mb-1">{new Date(post.postDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                    <div className={`transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${expanded ? 'opacity-100 max-h-[300px] mt-2 mb-2' : 'opacity-0 max-h-0 m-0 overflow-hidden'}`}> 
                      {post.description}
                      {/* Eliminar completamente la visualización de imágenes extra en móvil */}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* VERSIÓN ESCRITORIO */}
      {isReadyToFetch && (
        <div className="hidden sm:block">
          <div
            className="bg-[#F8F9FA] min-h-screen w-full font-futura px-2 sm:px-8 pb-12 relative transition-all duration-350 ease-in-out"
            style={{
              opacity: isExiting ? 0 : isLoaded ? 1 : 0
              // overflow: 'hidden'
            }}
          >
            {/* Estilos CSS para el selector de mes */}
            <style>{monthSelectorStyles}</style>
            
            {/* Botón Cerrar sesión */}
            <div className="absolute top-6 right-10 z-30 flex gap-4">
              <button 
                onClick={handleLogout}
                className="px-8 py-3 rounded-full bg-white shadow text-[#44aaf6] font-bold tracking-wide text-lg border border-white hover:bg-blue-50 transition-all" 
                style={{letterSpacing: '0.08em'}}
              >
                Cerrar sesión
              </button>
            </div>
            {/* Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center pt-10 pb-8 max-w-7xl mx-auto">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#5AB2F7] leading-tight tracking-wide mb-6 lg:mb-0" style={{letterSpacing: '0.06em'}}>
                Ayúdanos a crear<br />tu contenido.
              </h1>
              <div className="lg:ml-12 max-w-xl w-full">
                <h2 className="text-3xl font-bold text-black mb-2">Elige la fecha de tu publicación</h2>
                <p className="text-sm text-black mb-1">Selecciona el día en que te gustaría que se publique este contenido en tus redes sociales.</p>
                <p className="text-sm text-black">Puedes elegir una fecha dentro de la próxima semana según tu calendario de publicaciones.</p>
                {/* PlanSelector para usuarios multi-plan (solo si no es adminViewUser) */}
                {!adminViewUser && (
                  <div className="hidden sm:block mt-8">
                    <PlanSelector onPlanChange={handlePlanChange} />
                  </div>
                )}
                {!adminViewUser && (
                  <div className="block sm:hidden">
                    <PlanSelector onPlanChange={handlePlanChange} />
                  </div>
                )}
                {adminViewUser && (
                  <div className="mt-4 p-3 rounded-xl bg-blue-50 text-blue-600 font-semibold text-base flex items-center gap-4">
                    <span>Viendo como: {adminViewUser.email}</span>
                    <button
                      onClick={() => navigate('/admin-users')}
                      className="px-4 py-1.5 rounded-full border border-[#44aaf6] text-[#44aaf6] font-semibold text-sm bg-white hover:bg-blue-50 transition-all shadow"
                    >
                      Cambiar usuario
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Selector de mes */}
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center justify-center space-x-6 w-64">
                <button
                  onClick={isPrevMonthDisabled ? undefined : handlePrevMonth}
                  className={`w-10 h-10 flex items-center justify-center rounded-full shadow text-2xl transition
                    ${isPrevMonthDisabled ? 'bg-gray-200 text-gray-300 cursor-not-allowed opacity-60' : 'bg-white text-gray-400 hover:bg-blue-50'}`}
                  disabled={isPrevMonthDisabled}
                  aria-disabled={isPrevMonthDisabled}
                >&#60;</button>
                <span
                  ref={monthTextRef}
                  className={`font-semibold font-helvetica-italic text-center whitespace-nowrap ${isCurrentMonth ? 'text-[#44aaf6]' : 'text-gray-800'}`}
                  style={{ minWidth: '120px' }}
                >
                  {MONTHS[month]} {year}
                </span>
                <button onClick={handleNextMonth} className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow text-2xl text-gray-400 hover:bg-blue-50 transition">&#62;</button>
              </div>
            </div>

            {/* Calendario */}
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-7 gap-4">
                {WEEK_DAYS.map((d, i) => (
                  <div key={i} className="text-center text-xs sm:text-base font-semibold text-gray-800 pb-2 border-b-2 border-gray-300 tracking-wide uppercase">
                    {d}
                  </div>
                ))}
              </div>
              {/* Renderizar los 5 calendarios, solo uno visible */}
              {monthsToRender.map(({ month: calMonth, year: calYear }, idx) => {
                const visible = calMonth === month && calYear === year;
                const days = getCalendarDays(calYear, calMonth);
                return (
                  <div
                    key={`${calYear}-${calMonth}`}
                    className={`grid grid-cols-7 gap-4 mt-2 transition-opacity duration-300 ${visible ? '' : 'hidden'}`}
                  >
                    {days.map((d, idx) => {
                      const dayNum = d.date.getDate();
                      const isCurrent = d.currentMonth;
                      // Buscar post real para este día
                      const post = posts.find(
                        (p: any) => {
                          const postDate = new Date(p.postDate);
                          return postDate.getFullYear() === d.date.getFullYear() &&
                            postDate.getMonth() === d.date.getMonth() &&
                            postDate.getDate() === d.date.getDate();
                        }
                      );
                      const isAnnotation = !!post;
                      const isBillingStart = billingStarts.includes(d.date.toISOString().slice(0, 10));
                      const isAutoScheduled = isScheduledDate(d.date);
                      // Detectar si es hoy
                      const isToday = (() => {
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        const dateToCheck = new Date(d.date);
                        dateToCheck.setHours(0,0,0,0);
                        return today.getTime() === dateToCheck.getTime();
                      })();
                      const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
                        if (isAnnotation) {
                          setSelectedAnnotation(post);
                          // Centrar el modal sobre la celda y evitar que se salga de la pantalla
                          const rect = e.currentTarget.getBoundingClientRect();
                          const modalWidth = 320; // Aproximado (w-80)
                          const modalHeight = 350; // Aproximado (imagen + padding)
                          let left = rect.left + rect.width / 2 + window.scrollX;
                          let top = rect.top + rect.height / 2 + window.scrollY;
                          top = top - modalHeight / 2;
                          if (top < 16) top = 16;
                          if (left - modalWidth / 2 < 8) left = modalWidth / 2 + 8;
                          const maxLeft = window.innerWidth - modalWidth / 2 - 8;
                          if (left > maxLeft) left = maxLeft;
                          const maxBottom = window.scrollY + window.innerHeight - 16;
                          if (top + modalHeight > maxBottom) {
                            top = maxBottom - modalHeight;
                            if (top < 16) top = 16;
                          }
                          setModalPosition({ top, left });
                        } else if (isPastOrToday) {
                          setShowDateWarning(true);
                        } else {
                          handleDayClick(null, d.date, isCurrent);
                        }
                      };

                      const isPastOrToday = d.date < new Date(new Date().setHours(0,0,0,0)) || d.date.toDateString() === new Date().toDateString();
                      return (
                        <div key={idx} className="relative group" onClick={handleClick}>
                          {/* La celda del día */}
                          <div
                            className={
                              `rounded-2xl shadow h-24 sm:h-28 w-full flex flex-col p-2 text-sm font-helvetica-italic relative transition-all duration-300 ` +
                              (isCurrent ? 'bg-white text-gray-700 ' : 'bg-white text-gray-300 opacity-70 ') +
                              (isAnnotation
                                ? 'bg-blue-50 border-2 border-blue-200 text-blue-400 '
                                : '') +
                              'cursor-pointer group-hover:shadow-[0_8px_20px_rgba(0,0,0,0.08)]' +
                              (isPastOrToday ? 'opacity-50 bg-gray-100 text-gray-400' : '')
                            }
                          >
                            <span className={
                              'font-helvetica-italic font-normal text-lg ' +
                              (isAnnotation ? 'font-bold text-blue-400' : '')
                            }>
                              {dayNum.toString().padStart(2, '0')}
                              {isBillingStart && (
                                <span className="ml-2 align-middle text-[#44aaf6] font-bold text-2xl relative group" style={{ cursor: 'pointer', display: 'inline-block' }}>
                                  •
                                  <span className="hidden sm:block absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200" style={{ minWidth: '120px' }}>
                                    <span className="relative bg-black text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap text-center block">
                                      Inicio de mensualidad (reseteo de posts)
                                      <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-5 border-r-5 border-t-5 border-l-transparent border-r-transparent border-t-black"></span>
                                    </span>
                                  </span>
                                </span>
                              )}
                            </span>
                            {/* Texto AUTO para fechas programadas automáticamente */}
                            {isAutoScheduled && !isAnnotation && (
                              <div className="text-[10px] text-blue-500 font-medium mt-1 leading-none">
                                AUTO.
                              </div>
                            )}
                            {/* Icono + centrado */}
                            {!isAnnotation && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                  <svg width="40" height="40" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="6" y="6" width="24" height="24" rx="10" stroke="#44aaf6" strokeWidth="2" fill="none" />
                                    <path d="M18 13V23" stroke="#44aaf6" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M13 18H23" stroke="#44aaf6" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </span>
                              </div>
                            )}
                            {/* Anotación */}
                            {isAnnotation && (
                              <div className="mt-1 text-xs text-left text-black font-semibold overflow-hidden">
                                <div className="flex items-start">
                                  {post.images && post.images.length > 0 && (
                                  <span className="inline-block align-middle mr-1.5 pt-0.5 flex-shrink-0">
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="stroke-current">
                                      <rect x="1" y="2" width="14" height="12" rx="2" strokeWidth="1.5"/>
                                      <circle cx="5.5" cy="6.5" r="1.5" strokeWidth="1.5"/>
                                      <path d="M10.5 14L8 11L15 4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </span>
                                  )}
                                  <span className="flex-1 leading-tight truncate">{post.title}</span>
                                </div>
                                <p className="text-gray-400 font-normal mt-1 truncate">{post.description}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
     
      {selectedAnnotation && (
        <AnnotationModal
          annotation={{
            ...selectedAnnotation,
            day: new Date(selectedAnnotation.postDate).getDate(),
            month: new Date(selectedAnnotation.postDate).getMonth(),
            year: new Date(selectedAnnotation.postDate).getFullYear()
          }}
          postId={selectedAnnotation.id}
          userId={selectedAnnotation.userId}
          onClose={handleCloseModal}
          position={modalPosition}
          onDelete={() => handleDeletePost(selectedAnnotation.id)}
          disableDelete={false}
          showAdminOption={currentUser?.role === 'ADMIN'}
        />
      )}
      {showLimitModal && (
        <LimitModal onClose={() => setShowLimitModal(false)} nextAvailableDate={nextAvailableDate} />
      )}
      {limitError && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center border-2 border-red-300">
            <div className="text-red-500 font-bold mb-2">{limitError}</div>
            <button onClick={() => setLimitError('')} className="mt-2 px-6 py-2 rounded-full bg-[#44aaf6] text-white font-bold text-lg shadow hover:bg-blue-600 transition-all">Cerrar</button>
          </div>
        </div>
      )}
      
      {/* Modal de confirmación de borrado */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={cancelDeletePost}
        onConfirm={confirmDeletePost}
        message={postToDelete ? '¿Estás seguro de que quieres eliminar esta publicación?' : ''}
        publicationTitle={postToDelete ? posts.find(p => p.id === postToDelete)?.title || '' : ''}
        subtitle="Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
      {showDateWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-30 transition-opacity duration-300" style={{animation: 'fadeIn 0.3s'}} />
          <div className="bg-white rounded-2xl shadow-xl p-7 max-w-xs w-full text-center flex flex-col items-center z-10 border-2 border-[#44aaf6] font-futura">
            <div className="bg-blue-50 rounded-full p-2 mb-3 flex items-center justify-center shadow-sm border-2 border-[#44aaf6]">
              <svg className="w-7 h-7" fill="none" stroke="#44aaf6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="text-[#44aaf6] font-semibold text-xl mb-1" style={{letterSpacing: '0.01em'}}>No puedes crear publicaciones aquí</div>
            <div className="text-gray-700 mb-5 text-sm leading-relaxed">Selecciona una fecha a partir del día de hoy</div>
            <button onClick={() => setShowDateWarning(false)} className="mt-1 px-6 py-2 rounded-full bg-[#44aaf6] text-white font-semibold text-base shadow-sm hover:bg-blue-600 transition-all border border-[#44aaf6]">Cerrar</button>
          </div>
          <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        </div>
      )}
      {showAdvanceWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-30 transition-opacity duration-300" style={{animation: 'fadeIn 0.3s'}} />
          <div className="bg-white rounded-2xl shadow-xl p-7 max-w-xs w-full text-center flex flex-col items-center z-10 border-2 border-[#44aaf6] font-futura">
            <div className="bg-blue-50 rounded-full p-2 mb-3 flex items-center justify-center shadow-sm border-2 border-[#44aaf6]">
              <svg className="w-7 h-7" fill="none" stroke="#44aaf6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="text-[#44aaf6] font-semibold text-xl mb-1" style={{letterSpacing: '0.01em'}}>No puedes crear publicaciones aquí</div>
            <div className="text-gray-700 mb-5 text-sm leading-relaxed">Las publicaciones deben prepararse con al menos 3 días de antelación</div>
            <button onClick={() => setShowAdvanceWarning(false)} className="mt-1 px-6 py-2 rounded-full bg-[#44aaf6] text-white font-semibold text-base shadow-sm hover:bg-blue-600 transition-all border border-[#44aaf6]">Cerrar</button>
          </div>
          <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        </div>
      )}
      {/* Modal de error al borrar */}
      {deleteError && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center border-2 border-red-300">
            <div className="text-red-500 font-bold mb-2">{deleteError}</div>
            <button onClick={() => setDeleteError(null)} className="mt-2 px-6 py-2 rounded-full bg-[#44aaf6] text-white font-bold text-lg shadow hover:bg-blue-600 transition-all">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarScreen; 

<style>{`
@keyframes fade-highlight {
  0% { background: #44aaf6; }
  100% { background: #fff; }
}
.animate-fade-highlight {
  animation: fade-highlight 2.4s forwards;
}
`}</style> 